import type { UpdateJwtCookieMessage } from '@gitlab/cross-origin-channel';

/**
 * Derive the parent domain for the cookie from the current hostname so the
 * updated cookie remains readable by all subdomains (e.g. app.unify.dev and
 * web-ide.unify.dev both share `.unify.dev`).
 *
 * Falls back to the current hostname for single-label hosts (localhost, etc.)
 * so local development doesn't break.
 */
const getCookieDomain = (): string => {
  const parts = window.location.hostname.split('.');
  if (parts.length < 2) return window.location.hostname;
  return '.' + parts.slice(-2).join('.');
};

const COOKIE_NAME = 'currentUser';
const EIGHT_DAYS_SECONDS = 8 * 24 * 60 * 60;

/**
 * Handle an `update-jwt-cookie` message from the extension worker.
 *
 * Reads the current `currentUser` cookie, replaces its `token` field with
 * the newly-refreshed JWT, and writes it back. This keeps the Next.js app
 * (app.unify.dev) and the Web IDE (web-ide.unify.dev) in sync on the same
 * shared `.unify.dev` domain cookie.
 */
export const handleUpdateJwtCookieMessage = (message: UpdateJwtCookieMessage): void => {
  const { token: newToken } = message.params;

  if (!newToken) {
    console.warn('[handleUpdateJwtCookieMessage] Received empty token — skipping cookie update.');
    return;
  }

  const raw = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${COOKIE_NAME}=`))
    ?.slice(COOKIE_NAME.length + 1);

  if (!raw) {
    console.warn('[handleUpdateJwtCookieMessage] currentUser cookie not found — skipping update.');
    return;
  }

  try {
    const currentUser = JSON.parse(decodeURIComponent(raw));
    currentUser.token = newToken;

    const domain = getCookieDomain();
    const value = encodeURIComponent(JSON.stringify(currentUser));

    document.cookie = [
      `${COOKIE_NAME}=${value}`,
      `domain=${domain}`,
      `path=/`,
      `max-age=${EIGHT_DAYS_SECONDS}`,
      `SameSite=Lax`,
    ].join('; ');

    console.log('[handleUpdateJwtCookieMessage] Cookie updated successfully.');
  } catch (err) {
    console.error('[handleUpdateJwtCookieMessage] Failed to update cookie:', err);
  }
};
